'use client';

import type { IconName } from '@polyseme/design-system';

import {
  Button,
  Card,
  CardContent,
  Icon,
  Typography,
} from '@polyseme/design-system';
import { cn } from '@polyseme/utils/cn/cn-utils';
import { useTranslations } from 'next-intl';

import { CategoryIcon } from './category-icon';

const UNIT = 1;

type CounterCardProps = {
  activeIconClass?: string;
  className?: string;
  description: string;
  icon: IconName;
  label: string;
  max?: number;
  min?: number;
  onChange: (value: number) => void;
  unit?: string;
  value: number;
};

export const CounterCard = ({
  activeIconClass = '',
  className,
  description,
  icon,
  label,
  max = 99,
  min = 0,
  onChange,
  unit,
  value = 0,
  ...props
}: CounterCardProps) => {
  const t = useTranslations('landing.cagnup.counterCard');

  const handleIncrement = () => {
    if (value < max) {
      onChange(value + UNIT);
    }
  };

  const handleDecrement = () => {
    if (value > min) {
      onChange(value - UNIT);
    }
  };

  const category = `${label} ${description}`.toLowerCase();

  return (
    <Card className={cn('bg-white', className)} {...props}>
      <CardContent className="flex h-full flex-col px-8 py-6 text-center">
        <CategoryIcon
          className={cn('mx-auto mb-2', {
            [activeIconClass]: value > 0,
          })}
          icon={icon}
        />

        <div className="mb-6 space-y-1">
          <Typography as="h3" variant="title-5">
            {label}
          </Typography>
          <Typography className="text-muted-foreground" variant="helper">
            {description}
          </Typography>
        </div>

        <div className="mt-auto flex items-center justify-center gap-4">
          <Button
            isIcon
            aria-label={t('decrement.aria-label', {
              unit: unit ?? '',
              category,
            })}
            className="border-brand"
            disabled={value <= min}
            variant="outline"
            onClick={handleDecrement}
          >
            <Icon className="size-5" name="minus" />
          </Button>

          <div className="min-w-16 text-center">
            <Typography variant="title-4">{value}</Typography>
            <Typography variant="body">{unit}</Typography>
          </div>

          <Button
            isIcon
            aria-label={t('increment.aria-label', {
              unit: unit ?? '',
              category,
            })}
            className="border-brand"
            disabled={value >= max}
            variant="outline"
            onClick={handleIncrement}
          >
            <Icon className="size-5" name="plus" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
